import React, { Suspense, lazy } from 'react';
import { useOutletContext, Navigate } from 'react-router-dom';
import { buildTheme } from '../design/theme';
import { SERIF, UI } from '../design/theme';
import { PERSONAS, getStoredPersona, DEFAULT_PERSONA } from './persona';

// Eager: HomeFeed is the default landing — load synchronously so the first
// paint isn't blocked by an extra chunk fetch.
import HomeFeed from './HomeFeed';

// Lazy-loaded heavy routes — each becomes its own webpack chunk so the
// initial bundle stays slim (Leaflet ~145 KB, PetDetailSheet, SwipeDeck,
// Pokdeng WS client etc. only download when the user actually navigates).
const CompanionHome     = lazy(() => import('./CompanionHome'));
const MeProfile         = lazy(() => import('./MeProfile'));
const MapV2             = lazy(() => import('./MapV2'));
const CommunityHub      = lazy(() => import('./CommunityHub'));
const ChallengeEventPage = lazy(() => import('./ChallengeEventPage'));
const V2AnimalProfile   = lazy(() => import('./V2AnimalProfile'));
const V2UserProfile     = lazy(() => import('./V2UserProfile'));
const QuickSpotPage      = lazy(() => import('./QuickSpotPage'));
const PokdengLobby      = lazy(() => import('./PokdengLobby'));
const CommunityCarePage = lazy(() => import('./CommunityCarePage'));
const WildlifePage      = lazy(() => import('./WildlifePage'));
const DebugPage         = lazy(() => import('./DebugPage'));
const AdminZone         = lazy(() => import('./AdminZone'));
const StampHuntPage     = lazy(() => import('./stamp-hunt/StampHuntPage'));

const DEFAULT_ADMIN_EMAILS = new Set(['intaka@hotmail.com', 'intaka@gmail.com']);

function useTheme() {
  const ctx = useOutletContext() || {};
  return ctx.theme || buildTheme('coral');
}

// Minimal full-screen fallback shown while a route chunk is downloading.
// Theme-aware so it doesn't flash white.
function RouteFallback({ theme }) {
  return (
    <div
      data-testid="v2-route-fallback"
      style={{
        minHeight: '100vh', background: theme.bg, color: theme.muted,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        fontFamily: UI, fontSize: 13,
      }}
    >
      <div style={{
        width: 22, height: 22, borderRadius: '50%',
        border: `3px solid ${theme.cardBorder}`,
        borderTopColor: theme.primary,
        animation: 'tlSpin 0.8s linear infinite',
      }}/>
    </div>
  );
}

const withFallback = (theme, node) => (
  <Suspense fallback={<RouteFallback theme={theme}/>}>{node}</Suspense>
);

export function HomeFeedRoute() {
  const theme = useTheme();
  const personaId = getStoredPersona() || DEFAULT_PERSONA;
  const persona = PERSONAS[personaId];
  if (persona && persona.home === 'companion') {
    return withFallback(theme, <CompanionHome theme={theme}/>);
  }
  return <HomeFeed theme={theme}/>;
}
export function MeProfileRoute()      { const t = useTheme(); return withFallback(t, <MeProfile theme={t}/>); }
export function MapV2Route()          { const t = useTheme(); return withFallback(t, <MapV2 theme={t}/>); }
export function CommunityHubRoute()   { const t = useTheme(); return withFallback(t, <CommunityHub theme={t}/>); }
export function ChallengeEventRoute() { const t = useTheme(); return withFallback(t, <ChallengeEventPage theme={t}/>); }
export function V2AnimalProfileRoute(){ const t = useTheme(); return withFallback(t, <V2AnimalProfile theme={t}/>); }
export function V2UserProfileRoute()  { const t = useTheme(); return withFallback(t, <V2UserProfile theme={t}/>); }
export function QuickSpotRoute()      { const t = useTheme(); return withFallback(t, <QuickSpotPage theme={t}/>); }
export function PokdengLobbyRoute()   { const t = useTheme(); return withFallback(t, <PokdengLobby theme={t}/>); }
export function CommunityCareRoute()  { const t = useTheme(); return withFallback(t, <CommunityCarePage theme={t}/>); }
export function WildlifeRoute()       { const t = useTheme(); return withFallback(t, <WildlifePage theme={t}/>); }
export function DebugRoute() {
  const t = useTheme();
  // Admin-only guard: redirect non-admins to home
  let isAdmin = false;
  try {
    const u = JSON.parse(localStorage.getItem('taillink_user') || 'null');
    isAdmin = !!(u && u.is_admin);
  } catch { /* noop */ }
  if (!isAdmin) return <Navigate to="/v2" replace />;
  return withFallback(t, <DebugPage theme={t}/>);
}

export function AdminZoneRoute() {
  const t = useTheme();
  let isAdmin = false;
  try {
    const u = JSON.parse(localStorage.getItem('taillink_user') || 'null');
    const email = String(u?.email || '').toLowerCase();
    isAdmin = !!(u && (u.is_admin || DEFAULT_ADMIN_EMAILS.has(email)));
  } catch { /* noop */ }
  if (!isAdmin) return <Navigate to="/v2" replace />;
  return withFallback(t, <AdminZone theme={t}/>);
}
export function StampHuntRoute()     { const t = useTheme(); return withFallback(t, <StampHuntPage theme={t}/>); }

export function V2PlaceholderRoute({ title, subtitle }) {
  const theme = useTheme();
  return (
    <div data-testid="v2-placeholder" style={{
      minHeight: '100vh', display: 'flex', flexDirection: 'column',
      alignItems: 'center', justifyContent: 'center',
      padding: '0 28px', background: theme.bg, color: theme.text, textAlign: 'center',
    }}>
      <div style={{ fontSize: 56, marginBottom: 16 }}>🐾</div>
      <div style={{ fontFamily: SERIF, fontSize: 28, fontWeight: 600, letterSpacing: -0.5 }}>{title}</div>
      <div style={{ fontFamily: UI, fontSize: 14, color: theme.muted, marginTop: 8, maxWidth: 280 }}>
        {subtitle}
      </div>
    </div>
  );
}
